import type { TFunction } from 'i18next';
import { StepConfig } from '../../dynamicForm/types/fieldTypes';
import makesData from '../../json/makes.json';
import {
  BODY_TYPES,
  CAR_COLORS,
  CONDITIONS,
  FUEL_TYPES,
  IMPORT_AVAILABILITY_OPTIONS,
  IMPORT_COUNTRY_OPTIONS,
  TRANSMISSIONS,
  YEAR_OPTIONS,
} from '../constants/listingOptions';

export const getVehicleDataStep = (t: TFunction): StepConfig => ({
  title: t('listing_steps.vehicle_info_title'),
  description: t('listing_steps.vehicle_info_desc'),
  sections: [
    {
      title: t('listing_steps.vehicle_info_title'),
      fields: [
        {
          name: 'isImported',
          type: 'checkbox',
          label: t('listing_steps.imported_vehicle'),
          layout: { span: 24 },
          customProps: {
            helpText: t('listing_steps.imported_hint'),
          },
        },
        {
          name: 'importCountry',
          type: 'select',
          label: t('listing_steps.label_import_country'),
          required: true,
          layout: { span: 12 },
          options: IMPORT_COUNTRY_OPTIONS,
          visible: (formData: Record<string, any>) => formData.isImported === true,
          customProps: { placeholder: t('listing_steps.ph_select_country'), showSearch: true },
        },
        {
          name: 'shippingIncluded',
          type: 'checkbox',
          label: t('listing_steps.label_shipping_included'),
          layout: { span: 12 },
          visible: (formData: Record<string, any>) => formData.isImported === true,
          customProps: { helpText: t('listing_steps.shipping_hint') },
        },
        {
          name: 'importAvailability',
          type: 'select',
          label: t('listing_steps.label_availability'),
          required: true,
          layout: { span: 12 },
          options: IMPORT_AVAILABILITY_OPTIONS as any,
          visible: (formData: Record<string, any>) => formData.isImported === true,
          customProps: { placeholder: t('listing_steps.ph_select_availability') },
        },
        {
          name: 'estimatedArrivalDate',
          type: 'date',
          label: t('listing_steps.label_arrival_date'),
          required: true,
          layout: { span: 12 },
          visible: (formData: Record<string, any>) =>
            formData.isImported === true && formData.importAvailability === 'READY_TO_IMPORT',
          customProps: { placeholder: t('listing_steps.ph_select_arrival') },
        },
        {
          name: 'title',
          type: 'text',
          label: t('listing_steps.label_title'),
          required: true,
          layout: { span: 24 },
          validations: [
            { type: 'minLength', message: t('listing_steps.validation_title_min'), value: 5 },
            { type: 'maxLength', message: t('listing_steps.validation_title_max'), value: 100 },
          ],
          customProps: {
            placeholder: t('listing_steps.ph_title_example'),
            maxLength: 100,
          },
        },
        {
          name: 'year',
          type: 'select',
          label: t('listing_steps.label_year'),
          required: true,
          layout: { span: 12 },
          options: YEAR_OPTIONS,
          customProps: { placeholder: t('listing_steps.ph_select_year') },
        },
        {
          name: 'make',
          type: 'select',
          label: t('listing_steps.label_make'),
          required: true,
          layout: { span: 12 },
          resetFields: ['model'],
          api: {
            url: '/api/vehicles/makes',
            mapResponse: (response: any) => {
              const makes: string[] = Array.isArray(response) ? response : [];
              return makes.map((makeName) => {
                const logoEntry = makesData.find(
                  (m: any) => m.makeName?.toLowerCase() === makeName?.toLowerCase()
                );
                return {
                  value: makeName,
                  label: makeName,
                  image: logoEntry?.image?.thumb || logoEntry?.image?.optimized || '',
                };
              });
            },
          },
          visible: (formData: Record<string, any>) => !!formData.year,
          customProps: {
            placeholder: t('listing_steps.ph_select_make'),
            allowRequest: true,
            requestType: 'make',
          },
        },
        {
          name: 'model',
          type: 'select',
          label: t('listing_steps.label_model'),
          required: true,
          layout: { span: 12 },
          api: {
            url: '/api/vehicles/models',
            params: (formData: Record<string, any>) => ({ make: formData.make }),
            mapResponse: (response: any) => {
              const models: string[] = Array.isArray(response) ? response : [];
              return models.map((m) => ({ value: m, label: m }));
            },
            dependencies: ['make'],
          },
          visible: (formData: Record<string, any>) => !!formData.make,
          customProps: {
            placeholder: t('listing_steps.ph_select_model'),
            allowRequest: true,
            requestType: 'model',
            requestContextMakeField: 'make',
          },
        },
        {
          name: 'price',
          type: 'number',
          label: t('listing_steps.label_price'),
          required: true,
          layout: { span: 12 },
          validations: [
            { type: 'min', message: t('listing_steps.validation_min_price'), value: 500 },
            { type: 'max', message: t('listing_steps.validation_max_price'), value: 1000000 },
          ],
          customProps: { placeholder: t('listing_steps.ph_price_example'), min: 500, max: 1000000 },
        },
        {
          name: 'isNegotiable',
          type: 'checkbox',
          label: t('listing_steps.price_negotiable'),
          layout: { span: 12 },
          customProps: { helpText: t('listing_steps.price_negotiable_hint') },
        },
        {
          name: 'mileage',
          type: 'number',
          label: t('listing_steps.label_mileage'),
          required: true,
          layout: { span: 12 },
          customProps: { placeholder: t('listing_steps.ph_mileage_example'), min: 0 },
        },
        {
          name: 'color',
          type: 'select',
          label: t('listing_steps.label_color'),
          required: true,
          layout: { span: 12 },
          options: CAR_COLORS,
          customProps: { placeholder: t('listing_steps.ph_select_color') },
        },
        {
          name: 'fuelType',
          type: 'select',
          label: t('listing_steps.label_fuel'),
          required: true,
          layout: { span: 12 },
          options: FUEL_TYPES,
          customProps: { placeholder: t('listing_steps.ph_select_fuel') },
        },
        {
          name: 'transmission',
          type: 'select',
          label: t('listing_steps.label_transmission'),
          required: true,
          layout: { span: 12 },
          options: TRANSMISSIONS,
          customProps: { placeholder: t('listing_steps.ph_select_transmission') },
        },
        {
          name: 'bodyType',
          type: 'select',
          label: t('listing_steps.label_body_type'),
          required: true,
          layout: { span: 12 },
          options: BODY_TYPES,
          customProps: { placeholder: t('listing_steps.ph_select_body_type') },
        },
        {
          name: 'condition',
          type: 'select',
          label: t('listing_steps.label_condition'),
          required: true,
          layout: { span: 12 },
          options: CONDITIONS,
          customProps: { placeholder: t('listing_steps.ph_select_condition') },
        },
      ],
    },
  ],
  footer: {
    buttons: [
      {
        label: t('listing_steps.btn_next_equipment'),
        action: 'next',
        position: 'right',
        variant: 'primary',
        icon: 'bi-arrow-right',
      },
    ],
  },
});

// Backward-compat export (uses keys as-is; consumers should migrate to getVehicleDataStep)
export const vehicleDataStep: StepConfig = getVehicleDataStep(((k: string) => k) as any);
